import { useSearchStore } from '../store/SearchStore';

const SUBSCRIPTION_KEY = import.meta.env.VITE_SYNAPSE_SUBSCRIPTION_KEY;
const ANALYTICS_API_URL = import.meta.env.VITE_SYNAPSE_ANALYTICS_API_URL;

type AnalyticsAction =
  | 'Initial Search'
  | 'Select Route'
  | 'Unselect Route'
  | 'Change Datetime'
  | 'Change Journey Type'
  | 'Change From'
  | 'Change To'
  | 'Start Navigation';

const appLoadTime = Date.now();
const lastActionTimes = new Map<AnalyticsAction, number>();

export const sendSynapseAnalytics = async (action: AnalyticsAction) => {
  if (Date.now() - appLoadTime < 100) {
    return;
  }

  const now = Date.now();
  const lastTime = lastActionTimes.get(action) || 0;
  if (now - lastTime < 100) {
    return;
  }
  lastActionTimes.set(action, now);

  const { directions, travelTime, travelType, selectedRoute, locationStart, locationEnd } = useSearchStore.getState();

  if (!locationStart || !locationEnd || !directions) {
    return;
  }

  const aq = selectedRoute?.airQuality;
  const lowRoute = directions?.routes?.[0];
  const midRoute = directions?.routes?.length === 3 ? directions?.routes[1] : undefined;
  const highRoute = directions?.routes?.length === 3 ? directions?.routes[directions.routes.length - 1] : undefined;

  const sessionId = sessionStorage.getItem('carl_session_id');

  const analytics = {
    addUserSearchObject: {
      action,
      host_source: 'WCC',
      referrer_url: window.location.href.split('?')[0],
      session_id: sessionId ? parseInt(sessionId, 10) : Math.floor(Math.random() * 9999999) + 1,
      search_datetime: new Date().toISOString(),
      journey_datetime: travelTime?.toISOString() || new Date().toISOString(),
      travel_method: travelType || '',
      from_latitude: locationStart?.location?.lat() || 0,
      from_longitude: locationStart?.location?.lng() || 0,
      to_latitude: locationEnd?.location?.lat() || 0,
      to_longitude: locationEnd?.location?.lng() || 0,
      route_rank_selected: aq?.label_ranking || 'Unselected',
      start_selected: selectedRoute ? 'Y' : 'N',
      high_route_distance: highRoute?.legs?.[0]?.distance?.value ? highRoute.legs[0].distance.value / 1000 : undefined,
      high_journey_time: highRoute?.legs?.[0]?.duration?.value ? highRoute.legs[0].duration.value / 60 : undefined,
      high_NO2: highRoute?.airQuality?.no2 || undefined,
      high_PM_25: highRoute?.airQuality?.['pm2.5'] || undefined,
      high_in_model_area: highRoute?.airQuality?.in_modelled_area,
      mid_route_distance: midRoute?.legs?.[0]?.distance?.value ? midRoute.legs[0].distance.value / 1000 : undefined,
      mid_journey_time: midRoute?.legs?.[0]?.duration?.value ? midRoute.legs[0].duration.value / 60 : undefined,
      mid_NO2: midRoute?.airQuality?.no2 || undefined,
      mid_PM_25: midRoute?.airQuality?.['pm2.5'] || undefined,
      med_in_model_area: midRoute?.airQuality?.in_modelled_area,
      low_route_distance: lowRoute?.legs?.[0]?.distance?.value ? lowRoute.legs[0].distance.value / 1000 : undefined,
      low_journey_time: lowRoute?.legs?.[0]?.duration?.value ? lowRoute.legs[0].duration.value / 60 : undefined,
      low_NO2: lowRoute?.airQuality?.no2 || undefined,
      low_PM_25: lowRoute?.airQuality?.['pm2.5'] || undefined,
      low_in_model_area: lowRoute?.airQuality?.in_modelled_area,
    },
  };

  if (import.meta.env.VITE_SKIP_ANALYTICS) {
    console.log(action, analytics.addUserSearchObject);
    return;
  }

  try {
    const response = await fetch(ANALYTICS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ocp-apim-subscription-key': SUBSCRIPTION_KEY,
      },
      body: JSON.stringify(analytics),
    });

    if (!response.ok) {
      throw new Error(`Failed to send analytics: ${response.status}`);
    }

    console.log('Analytics sent successfully');
  } catch (error) {
    console.error('Error sending analytics:', error);
  }
};
